import React, { useState, useEffect, useRef, useContext } from 'react';
import { useMountedState } from 'react-use';
import block from 'propmods';
import { TextInput } from 'lego-on-react';
import { getPhraseCommonness } from '../../../api';
import { PhraseCommonness } from '../../../types';
import WithSpinner from '../../Loading/WithSpinner/WithSpinner';
import { getDefaultSkillName } from '../../../utils/settings/alice-skill';
import { FormFieldContext } from '../../../context/formFieldContext';

import './TextInputWithCommonness.scss';

const b = block('TextInputWithCommonness');

interface TextInputWithCommonnessProps {
    value: string;
    disabled?: boolean;
    onChange: (val: string) => void;
    spinnerGapRight?: boolean;
    hideCommonness?: boolean;
    fieldLabel: string;
}

const TextInputWithCommonness: React.FC<TextInputWithCommonnessProps> = ({
    value,
    spinnerGapRight,
    onChange,
    hideCommonness,
    fieldLabel,
    ...restProps
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const inputRef: React.LegacyRef<TextInput & { state: { focused: object } }> = useRef(null);
    const { setNonValidationError } = useContext(FormFieldContext);
    const isMounted = useMountedState();

    const getCommonness = async () => {
        if (!value || restProps.disabled || value === getDefaultSkillName() || hideCommonness) {
            setNonValidationError('');
            return;
        }

        setIsLoading(true);

        try {
            const commonness = await getPhraseCommonness(value);
            if (isMounted() && commonness === PhraseCommonness.Red) {
                setNonValidationError(
                    <>
                        {fieldLabel} слишком общее. Переформулируйте его, либо подтвердите{' '}
                        <a href="#brandVerificationWebsite">права на&nbsp;бренд</a>.
                    </>,
                );
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setNonValidationError('');
        if (inputRef.current && !inputRef.current.state.focused) {
            void getCommonness();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, hideCommonness]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void getCommonness(), []);

    return (
        <div {...b()}>
            <WithSpinner isLoading={isLoading} leftOffset={spinnerGapRight ? 72.5 : undefined}>
                <TextInput
                    theme="normal"
                    size="s"
                    onBlur={getCommonness}
                    text={value}
                    onChange={(text: string) => {
                        setNonValidationError('');
                        onChange(text);
                    }}
                    ref={inputRef}
                    autocomplete={false}
                    {...restProps}
                />
            </WithSpinner>
        </div>
    );
};

export default TextInputWithCommonness;
